import * as React from "react";
import {
    Autocomplete,
    Button,
    Checkbox,
    Drawer,
    ListSubheader,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { useTranslation } from "react-i18next";
import { HEADINGS } from "../meteringGraphql";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

// options: [{ id, name, heading }], sorted by heading. selected: [id], which
// may include IDs of other drawers' options; those are left as they are.
export default function MeteringSelectionDrawer({
    open,
    onClose,
    label,
    options,
    selected,
    setSelected,
}) {
    const { t } = useTranslation();

    const optionIds = options.map((option) => option.id);

    const selectedOptions = options.filter((option) =>
        selected.includes(option.id),
    );

    // One button per heading: select all of it, or none once all are selected.
    const handleHeadingButton = (heading) => {
        const ids = options
            .filter((option) => option.heading === heading)
            .map((option) => option.id);
        const allSelected = ids.every((id) => selected.includes(id));
        setSelected(
            allSelected
                ? selected.filter((id) => !ids.includes(id))
                : [...new Set([...selected, ...ids])],
        );
    };

    return (
        <Drawer anchor="right" open={open} onClose={onClose}>
            <Stack spacing={3} padding={2}>
                <Typography variant="h6" color="primary">
                    {t("Filter")}
                </Typography>
                <Autocomplete
                    multiple
                    disableCloseOnSelect
                    limitTags={5}
                    value={selectedOptions}
                    onChange={(event, newValue) =>
                        setSelected([
                            ...selected.filter((id) => !optionIds.includes(id)),
                            ...newValue.map((option) => option.id),
                        ])
                    }
                    options={options}
                    groupBy={(option) => option.heading}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderGroup={(params) => {
                        const allSelected = options
                            .filter((option) => option.heading === params.group)
                            .every((option) => selected.includes(option.id));
                        return (
                            <li key={params.key}>
                                <ListSubheader
                                    component="div"
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                    }}
                                >
                                    {t(HEADINGS[params.group] ?? params.group)}
                                    <Button
                                        size="small"
                                        // Keep the list open when the button is clicked.
                                        onMouseDown={(event) => event.preventDefault()}
                                        onClick={() => handleHeadingButton(params.group)}
                                    >
                                        {allSelected ? t("Select none") : t("Select all")}
                                    </Button>
                                </ListSubheader>
                                <ul style={{ padding: 0 }}>{params.children}</ul>
                            </li>
                        );
                    }}
                    renderInput={(params) => (
                        <TextField {...params} label={label} />
                    )}
                    renderOption={(props, option, { selected: isSelected }) => (
                        <li {...props} key={option.id}>
                            <Checkbox
                                icon={icon}
                                checkedIcon={checkedIcon}
                                style={{ marginRight: 8 }}
                                checked={isSelected}
                            />
                            {option.name}
                        </li>
                    )}
                    sx={{ width: 500 }}
                />
            </Stack>
        </Drawer>
    );
}
